import dayjs from "dayjs";
import cloneDeep from "lodash/cloneDeep";
import difference from "lodash/difference";
import services from "../services";

const strict = true;

const state = {
  appointment: {},
  memberContactInfo: {},
  appointmentContactInfo: {},
  communications: [],
  office: {},
  slots: [],
  slotDuration: 0,
  offset: 0,
  bookableMonths: [],
  noteText: "",
  serviceTimetable: [],
  portalDays: {},
  portalDates: [],
  /**
   * The configuration for the TIMEPERIOD
   */
  morningTimeFrom: "07:00:00",
  afternoonTimeTo: "22:00:00",
  allPeriodSlots: []
};

const getters = {
  appointment: state => state.appointment,
  personalNote: state => state.appointment.personalNote,
  memberContactInfo: state => state.memberContactInfo,
  appointmentContactInfo: state => state.appointmentContactInfo,
  communications: state => state.communications,
  office: state => state.office,
  slots: state => state.slots,
  slotDuration: state => state.slotDuration,
  offset: state => state.offset,
  bookableMonths: state => state.bookableMonths,
  noteText: state => state.noteText,
  serviceTimetable: state => state.serviceTimetable,
  portalDays: state => state.portalDays,
  portalDates: state => state.portalDates,
  allPeriodSlots: state => state.allPeriodSlots
};

const mutations = {
  appointment(state, appointment) {
    state.appointment = appointment;
  },
  contactInfo(state, contactInfo) {
    let mci = {
      memberContactingChannel: contactInfo.memberContactingChannel,
      memberPreferredContactType: contactInfo.memberPreferredContactType,
      memberVoucherChannelType: contactInfo.memberVoucherChannelType
    };
    let aci = {
      appointmentContactingChannel: contactInfo.appointmentContactingChannel,
      appointmentPreferredContactType:
        contactInfo.appointmentPreferredContactType,
      appointmentVoucherChannelType: contactInfo.appointmentVoucherChannelType
    };
    state.memberContactInfo = mci;
    state.appointmentContactInfo = aci;
    state.communications = contactInfo.communications;
  },
  office(state, office) {
    state.office = office;
  },
  slots(state, slots) {
    state.slots = slots;
  },
  slotDuration(state, slotDuration) {
    state.slotDuration = slotDuration;
  },
  offset(state, offset) {
    state.offset = offset;
  },
  bookableMonths(state, months) {
    state.bookableMonths = months;
  },
  setNoteText(state, value) {
    state.noteText = value;
  },
  setServiceTimetable(state) {
    let serviceTimetable = [...state.office.nestCalendar.serviceTimeTable];
    let isDoctorConstraint =
      state.appointment.appointmentFlowSearchType === "BY_DOCTOR";
    if (isDoctorConstraint) {
      let docId = state.appointment.doctorId;
      serviceTimetable = serviceTimetable.filter(stt => {
        return stt.doctorId == docId;
      });
    }
    serviceTimetable = serviceTimetable.reduce((a, b) => {
      if (!a[b.day]) a[b.day] = [];
      let [startHH, startMM] = b.fromHHMM.split(":");
      let newStartMM = null;
      if (Number(startMM) > 0 && Number(startMM) < 30) {
        newStartMM = "00";
      } else if (Number(startMM) > 30 && Number(startMM) <= 59) {
        newStartMM = "30";
      } else {
        newStartMM = startMM;
      }
      let newFrom = [startHH, newStartMM].join(":");
      b["newFromHHMM"] = newFrom;
      let [endHH, endMM] = b.toHHMM.split(":");
      let newEndHH = null;
      let newEndMM = null;
      if (Number(endMM) > 0 && Number(endMM) < 30) {
        newEndHH = endHH;
        newEndMM = "30";
      } else if (Number(endMM) > 30 && Number(endMM) <= 59) {
        newEndHH = Number(endHH) + Number(1);
        newEndMM = "00";
      } else {
        newEndHH = endHH;
        newEndMM = endMM;
      }
      let newTo = [newEndHH, newEndMM].join(":");
      b["newToHHMM"] = newTo;
      [startHH, startMM] = b.newFromHHMM.split(":");
      let startDate = dayjs()
        .set("hour", Number(startHH))
        .set("minute", Number(startMM));
      [endHH, endMM] = b.newToHHMM.split(":");
      let endDate = dayjs()
        .set("hour", Number(endHH))
        .set("minute", Number(endMM))
        .subtract(29, "minute");
      let slots = [];
      while (startDate.isBefore(endDate)) {
        let newDate = startDate.add(30, "minute");
        slots.push(`${startDate.format("HH:mm")} - ${newDate.format("HH:mm")}`);
        startDate = newDate;
      }
      b["slots"] = slots;
      a[b.day].push(b);
      return a;
    }, {});
    state.serviceTimetable = serviceTimetable;
  },
  setPortalDays(state, { newDates, dates }) {
    let portalDays = { ...state.portalDays };
    let result = {};
    if (newDates.length < dates.length) {
      let diffDates = difference(dates, newDates);
      result = diffDates.reduce((a, b) => {
        let key = dayjs(b).format("MMMM YYYY - dddd");
        let idx = a[key].findIndex(d => d.date === b);
        if (idx > -1) {
          a[key].splice(idx, 1);
        }
        if (!a[key].length) {
          delete a[key];
        } else {
          a[key].sort((c, d) =>
            dayjs(c.date).isAfter(dayjs(d.date)) ? 1 : -1
          );
        }
        return a;
      }, portalDays);
    } else {
      let timeTables = state.serviceTimetable;
      let diffDates = difference(newDates, dates);
      result = diffDates.reduce((a, b) => {
        let key = dayjs(b).format("MMMM YYYY - dddd");
        if (!a[key]) a[key] = [];
        let day = dayjs(b).locale("en").format("dddd").toUpperCase();
        let newDate = {
          day,
          date: b,
          timetables: timeTables[day] ? cloneDeep(timeTables[day]) : []
        };
        a[key].push(newDate);
        a[key].sort((c, d) => (dayjs(c.date).isAfter(dayjs(d.date)) ? 1 : -1));
        return a;
      }, portalDays);
    }
    state.portalDates = newDates;
    state.portalDays = result;
  },
  updateDateSlots(state, { period, selectedSlots }) {
    let portalDays = { ...state.portalDays };
    let key = dayjs(period.date).format("MMMM YYYY - dddd");
    let p = portalDays[key].find(md => md.date === period.date);
    if (p) {
      p.timetables.forEach(function (tt) {
        if (tt && !tt.orignalSlots) {
          tt["orignalSlots"] = tt.slots;
        }
        let newSlots = [];
        tt.orignalSlots.forEach(function (s) {
          let found = selectedSlots.find(slot => slot.range === s);
          if (found !== undefined) {
            newSlots.push(s);
          }
        });
        if (newSlots.length > 0) {
          tt.slots = newSlots;
          tt["changed"] = true;
        } else {
          tt["changed"] = false;
        }
      });
    }
    state.portalDays = portalDays;
  },
  updateWeekDaySlots(state, { period, selectedSlots }) {
    let portalDays = { ...state.portalDays };
    let weekday = dayjs(period.date).locale("it").format("dddd");
    let weekdayKeys = Object.keys(portalDays).filter(mdKey =>
      mdKey.includes(weekday)
    );
    weekdayKeys.forEach(wdKey => {
      portalDays[wdKey].forEach(md => {
        md.timetables.forEach(function (tt) {
          if (tt && !tt.orignalSlots) {
            tt["orignalSlots"] = tt.slots;
          }
          let newSlots = [];
          tt.orignalSlots.forEach(function (s) {
            let found = selectedSlots.find(slot => slot.range === s);
            if (found !== undefined) {
              newSlots.push(s);
            }
          });
          if (newSlots.length > 0) {
            tt.slots = newSlots;
            tt["changed"] = true;
          } else {
            tt["changed"] = false;
          }
        });
      });
    });
    state.portalDays = portalDays;
  },
  resetPortalProperties(state) {
    state.portalDays = {};
    state.portalDates = [];
  },
  resetAllPeriodSlots(state) {
    let slots = [];
    let [startHH, startMM, startSS] = state.morningTimeFrom.split(":");
    let startDate = dayjs()
      .set("hour", Number(startHH))
      .set("minute", Number(startMM))
      .set("second", Number(startSS));
    let [endHH, endMM, endSS] = state.afternoonTimeTo.split(":");
    let endDate = dayjs()
      .set("hour", Number(endHH))
      .set("minute", Number(endMM))
      .set("second", Number(endSS));
    while (startDate.isBefore(endDate)) {
      let newDate = startDate.add(30, "minute");
      slots.push({
        range: `${startDate.format("HH:mm")} - ${newDate.format("HH:mm")}`,
        disabled: true
      });
      startDate = newDate;
    }
    if (slots.length === 31) {
      slots.pop();
    }
    state.allPeriodSlots = slots;
  },
  reset(state) {
    state.appointment = {};
    state.office = {};
    state.slots = [];
    state.slotDuration = 0;
    state.offset = 0;
    state.bookableMonths = [];
    state.noteText = "";
  }
};

const actions = {
  async initialize({ commit, dispatch }, appointmentId) {
    try {
      commit("loading/openLoadingDialog", null, { root: true });
      commit("appointment", {});
      commit("contactInfo", {});
      await dispatch("notifications/getNotificationsUnreadCount", null, {
        root: true
      });
      let appointment = await dispatch(
        "appointments/getAppointment",
        appointmentId,
        { root: true }
      );
      commit("appointment", appointment);
      commit("loading/openLoadingDialog", null, { root: true });
      let contactInfo = await dispatch(
        "appointments/getMemberContactInfo",
        appointmentId,
        { root: true }
      );
      commit("contactInfo", contactInfo);
    } catch (error) {
      commit("error/setError", error, { root: true });
    } finally {
      commit("loading/reset", null, { root: true });
    }
  },
  async initializeForMobileApp({ commit, dispatch }, appointmentId) {
    try {
      commit("loading/openLoadingDialog", null, { root: true });
      commit("appointment", {});
      commit("contactInfo", {});
      await dispatch(
        "notifications/getNotificationsUnreadCountForMobileApp",
        null,
        {
          root: true
        }
      );
      let appointment = await dispatch(
        "appointments/getAppointmentForMobileApp",
        appointmentId,
        { root: true }
      );
      commit("appointment", appointment);
      let contactInfo = await dispatch(
        "appointments/getMemberContactInfoForMobileApp",
        appointmentId,
        { root: true }
      );
      commit("contactInfo", contactInfo);
    } catch (error) {
      commit("error/setError", error, { root: true });
    } finally {
      commit("loading/reset", null, { root: true });
    }
  },
  async getOfficeService({ commit, getters }) {
    try {
      commit("loading/openLoadingDialog", null, { root: true });
      commit("office", {});
      let appointment = getters["appointment"];
      let office = await services.bookingsService.getOfficeService({
        officeserviceId: appointment.hfOfficeServiceId
      });
      commit("office", office);
    } catch (error) {
      commit("error/setError", error, { root: true });
    } finally {
      commit("loading/reset", null, { root: true });
    }
  },
  async getModificationData({ commit, getters, dispatch }) {
    try {
      commit("loading/openLoadingDialog", null, { root: true });
      let appointment = getters["appointment"];
      if (appointment.appointmentType === "SLOT") {
        await dispatch("getSlots", 0);
      }
    } catch (error) {
      commit("error/setError", error, { root: true });
    } finally {
      commit("loading/reset", null, { root: true });
    }
  },
  async getSlots({ commit, getters }, monthOffset) {
    try {
      commit("loading/openLoadingDialog", null, { root: true });
      let appointment = getters["appointment"];
      let nextAvailableMonth;
      let res;
      do {
        res = await services.bookingsService.getSlotsForMonths({
          monthOffset: monthOffset,
          officeserviceId: appointment.hfOfficeServiceId,
          obese:
            typeof appointment.contsraints === "object"
              ? appointment.contsraints.obese
              : false,
          doctorId: appointment.doctorId || null,
          claustrophobia:
            typeof appointment.contsraints === "object"
              ? appointment.contsraints.claustrophobia
              : false
        });
        if (res.slotList && res.slotList.length === 0) {
          let bookableMonths = getters["bookableMonths"];
          let thisMonth = dayjs().set("date", 1);
          let requestedMonth = thisMonth.add(monthOffset, "month");
          nextAvailableMonth = bookableMonths.find(m =>
            requestedMonth.isBefore(dayjs(`${m.year}-${m.month}-01`))
          );
          monthOffset = nextAvailableMonth
            ? Math.abs(
                dayjs(
                  `${nextAvailableMonth.year}-${nextAvailableMonth.month}-01`
                ).month() - thisMonth.month()
              )
            : monthOffset;
          commit("slots", []);
        } else {
          commit("offset", monthOffset);
          commit("slots", res.slotList);
          commit("slotDuration", res.duration.value);
        }
      } while (res.slotList.length === 0 && nextAvailableMonth !== undefined);
    } catch (error) {
      commit("error/setError", error, { root: true });
    } finally {
      commit("loading/reset", null, { root: true });
    }
  },
  async getBookableMonths({ commit, getters, rootGetters }) {
    try {
      commit("loading/openLoadingDialog", null, { root: true });
      let bookableMonths = [];
      let serviceId = getters["appointment"].serviceId;
      let bookingInfo = await services.coverageService.getServiceBookingInfo({
        id: serviceId,
        memberFiscalCode: rootGetters["login/userInfo"].codiceFiscale
      });
      if (Object.keys(bookingInfo).length) {
        let thisYear = dayjs().year();
        bookableMonths.push(
          ...bookingInfo[`${thisYear}`].coverage.map(month => ({
            year: thisYear,
            isBookable: month.bookableByRule && !month.notCovered,
            ...month
          }))
        );
        let nextYear = bookingInfo[`${thisYear + 1}`];
        if (nextYear) {
          bookableMonths.push(
            ...nextYear.coverage.map(month => ({
              year: thisYear + 1,
              isBookable: month.bookableByRule && !month.notCovered,
              ...month
            }))
          );
        }
        bookableMonths = bookableMonths.filter(month => month.isBookable);
        commit("bookableMonths", bookableMonths);
        return bookableMonths;
      }
    } catch (error) {
      commit("error/setError", error, { root: true });
    } finally {
      commit("loading/reset", null, { root: true });
    }
  },
  async changeAppointmentDate({ commit, getters, dispatch }) {
    try {
      commit("loading/openLoadingDialog", null, { root: true });
      let appointment = getters["appointment"];
      let appId = appointment.appointmentId;
      let dates = getters["portalDays"];
      let memberRequestDateWithTimes = buildRequestDateWithTimes(dates);

      let res = await dispatch("changeMemberAvailability", {
        appointmentId: appId,
        memberRequestDateWithTimes
      });

      let newAppointmentId = res.appointmentId;
      let successMessage = "dialog.success.dateModificationSucceededTP";
      commit(
        "success/openSuccessDialog",
        {
          title: "dialog.success.dateModification",
          message: successMessage,
          btnText: "exit",
          btnAction: () => {
            commit("success/reset", null, {
              root: true
            });
            dispatch("initialize", newAppointmentId);
          }
        },
        { root: true }
      );
    } catch (error) {
      commit("error/setError", error, { root: true });
    } finally {
      commit("loading/reset", null, { root: true });
    }
  },
  async changeSlotAppointmentDate({ commit }, { appointmentId, dateTime }) {
    return services.appointmentService.modifySlotDate({
      id: appointmentId,
      dateAndTime: dateTime
    });
  },
  async changeExpostAppointmentDate({ commit }, { appointmentId, dateTime }) {
    return services.appointmentService.modifyExpostDate({
      id: appointmentId,
      dateAndTime: dateTime
    });
  },
  async changeTimeperiodAppointmentDate(
    { commit },
    { appointmentId, dateTime }
  ) {
    let formattedDate = dayjs(dateTime).format("YYYY-MM-DD HH:mm").split(" ");
    let requestedDates = [];
    let obj = {
      day: formattedDate[0],
      fromTime: `${formattedDate[1]}:00`,
      toTime: null
    };
    requestedDates.push(obj);
    return services.appointmentService.modifyTimeperiodDate({
      id: appointmentId,
      memberRequestDateWithTimes: requestedDates
    });
  },
  async changeMemberAvailability(
    { commit },
    { appointmentId, memberRequestDateWithTimes }
  ) {
    return services.appointmentService.modifyDateForMemberAvailability({
      id: appointmentId,
      memberRequestDateWithTimes
    });
  },
  async setExistingNote({ commit, getters }) {
    try {
      commit("loading/openLoadingDialog", null, { root: true });
      let personalNote = getters["personalNote"];
      if (personalNote?.note) {
        commit("setNoteText", personalNote.note);
      }
    } catch (error) {
      commit("error/setError", error, { root: true });
    } finally {
      commit("loading/reset", null, { root: true });
    }
  },
  async savePersonalNote({ commit, getters }, obj) {
    try {
      commit("loading/openLoadingDialog", null, { root: true });
      let payload = {};
      payload["officeId"] = getters["appointment"].hfOfficeId;
      payload["noteId"] = getters["personalNote"]
        ? getters["personalNote"].noteId
        : null;
      payload["note"] = obj.note;
      let now = dayjs();
      let date = dayjs(now).format("YYYY-MM-DD");
      let hour = dayjs(now).hour();
      let minute = dayjs(now).minute();
      let dateTime = dayjs(`${date}T${hour}:${minute}`).format();
      payload["dateAndTime"] = dateTime;
      await services.appointmentService.callPostNote(payload);
    } catch (error) {
      commit("error/setError", error, { root: true });
    } finally {
      commit("loading/reset", null, { root: true });
    }
  },
  async reloadAppointment({ commit, dispatch }, appointmentId) {
    try {
      commit("loading/openLoadingDialog", null, { root: true });
      await dispatch("initialize", appointmentId);
    } catch (error) {
      commit("error/setError", error, { root: true });
    } finally {
      commit("loading/reset", null, { root: true });
    }
  },
  async resetAllPeriodSlots({ commit }) {
    await commit("resetAllPeriodSlots");
  }
};

const buildRequestDateWithTimes = dates => {
  let requestDateWithTimes = [];
  let keys = Object.keys(dates);
  keys.forEach(dKey => {
    dates[dKey].forEach(day => {
      let date = dayjs(day.date).format("YYYY-MM-DD");
      day.timetables.forEach(tt => {
        if (tt.changed) {
          if (tt.slots.length > 0) {
            tt.slots.forEach(slot => {
              let slotArray = slot.split(" - ");
              let from = slotArray[0];
              let to = slotArray[1];
              let obj = {
                day: date,
                fromTime: from.concat(":00"),
                toTime: to.concat(":00")
              };
              requestDateWithTimes.push(obj);
            });
          } else {
            let from = tt.fromHHMM.substring(0, tt.fromHHMM.lastIndexOf(":"));
            let to = tt.toHHMM.substring(0, tt.toHHMM.lastIndexOf(":"));
            let obj = {
              day: date,
              fromTime: `${from}:00`,
              toTime: `${to}:00`
            };
            requestDateWithTimes.push(obj);
          }
        }
      });
    });
  });
  requestDateWithTimes.sort((a, b) => {
    let aDate = new Date(a.day + " " + a.fromTime);
    let bDate = new Date(b.day + " " + b.fromTime);
    return aDate.getTime() - bDate.getTime();
  });
  return requestDateWithTimes;
};

export default {
  namespaced: true,
  strict,
  state,
  getters,
  mutations,
  actions
};
